<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { BuildingVisibleToEntry } from "@types"

defineOptions({
    name: "ItemBuilding",
})

// ================================
// TYPES & INTERFACES
// ================================
export interface ItemBuildingData {
    _id: string
    name?: string
    niveau?: string
    address?: {
        street?: string
        zipcode?: string
        city?: string
        /** Libellé unique (ex. API relation) : prioritaire sur rue + CP + ville. */
        inline_name?: string
    }
    /** Libellés des tags (normalisés côté parent depuis `Building.tags`). */
    tags?: string[]
    /** Personnes ayant accès (champ `Building.visible_to`). */
    visible_to?: BuildingVisibleToEntry[]
    /**
     * Droits utilisateur sur le bâtiment (ex. `["views"]`, `["owns"]`).
     * Sert à afficher le libellé « Partagé » lorsque seul le droit consultation est présent.
     */
    rights?: string[]
}

interface Props {
    /**
     * Données du bâtiment à afficher
     */
    building: ItemBuildingData
    /**
     * Affiche les tags sous l’adresse (désactivé si le parent masque les tags).
     */
    showTags?: boolean
    /**
     * Racine `<button type="button">` (réinitialisée) pour le même clic natif que les lignes dossier + vue-dnd-kit.
     */
    nativeButton?: boolean
}

interface Emits {
    /**
     * Émis lors du clic sur le bâtiment
     * @param buildingId - Identifiant du bâtiment
     * @param ev - Événement pointer (présent si interaction native, ex. bouton)
     */
    (e: "click", buildingId: string, ev?: MouseEvent): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    showTags: true,
    nativeButton: false,
})
const emit = defineEmits<Emits>()

const { t } = useI18n({
    messages: {
        fr: {
            header: {
                views: "Partagé",
                sharedBuildingHint:
                    "Ce bâtiment vous a été partagé : certaines fonctionnalités ne sont pas accessibles.",
            },
        },
    },
})

/** Au-delà de ce nombre, on affiche 3 avatars puis un badge +N. */
const VISIBLE_TO_OVERFLOW_THRESHOLD = 4
/** Nombre d’avatars « pleins » avant le badge +N lorsque la liste déborde. */
const VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW = 3

const ROLES_TO_HIDE = ["owner", "administrateur"]

type AccessAvatarSlot = {
    key: string
    image: string | undefined
    label: string | undefined
    ariaLabel: string
    entry: BuildingVisibleToEntry
}

// ================================
// COMPUTED
// ================================
const getTagLabels = computed(() => props.building.tags ?? [])

/**
 * Utilisateur avec uniquement le droit de consultation (bâtiment partagé).
 */
const getHasOnlyViewsRights = computed(() => {
    const rights = props.building.rights
    return (
        Array.isArray(rights) &&
        rights.length > 0 &&
        rights.every((r) => r === "views")
    )
})

const getVisibleToEntries = computed(() => {
    const entries =
        props.building.visible_to?.filter(
            (e) => !ROLES_TO_HIDE.includes(e.role ?? "")
        ) ?? []
    // On retire les doublons sur l'email (première occurrence conservée)
    const seenEmails = new Set<string>()
    return entries.filter((e) => {
        if (!e.username) return true
        if (seenEmails.has(e.username)) return false
        seenEmails.add(e.username)
        return true
    })
})

/**
 * Indique si le groupe d’avatars d’accès doit être affiché.
 */
const getShowAccessAvatars = computed(
    () => getVisibleToEntries.value.length > 0
)

/**
 * Avatars à afficher (images ou initiales) et éventuel débordement +N.
 */
const getAccessAvatarDisplay = computed(() => {
    const entries = getVisibleToEntries.value
    const n = entries.length
    if (n === 0) {
        return { slots: [] as AccessAvatarSlot[], overflowExtra: 0 }
    }
    if (n <= VISIBLE_TO_OVERFLOW_THRESHOLD) {
        return {
            slots: entries.map((e, i) => toAccessAvatarSlot(e, i)),
            overflowExtra: 0,
        }
    }
    const head = entries.slice(0, VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW)
    return {
        slots: head.map((e, i) => toAccessAvatarSlot(e, i)),
        overflowExtra: n - VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW,
    }
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Convertit le niveau du bâtiment en type d'icône
 * @param niveau - Niveau du bâtiment (groupe, appartement, maison, etc.)
 * @returns Type d'icône compatible avec BaseBuildingIcon
 */
function getBuildingIconType(niveau?: string): string {
    if (!niveau) return "default"
    const niveauLower = niveau.toLowerCase()
    if (niveauLower.includes("groupe")) return "groupe"
    if (niveauLower.includes("appartement")) return "appartement"
    if (niveauLower.includes("maison")) return "maison"
    return "default"
}

/**
 * Nom affiché (prénom + nom).
 */
function getAccessPersonFullName(entry: BuildingVisibleToEntry): string {
    const fn = (entry.firstname ?? "").trim()
    const ln = (entry.lastname ?? "").trim()
    return [fn, ln].filter(Boolean).join(" ").trim()
}

/**
 * Texte du tooltip : nom puis email (ligne suivante).
 */
function getAccessPersonTooltipText(entry: BuildingVisibleToEntry): string {
    const name = getAccessPersonFullName(entry)
    const email = (entry.username ?? "").trim()
    if (name && email) {
        return `${name}\n${email}`
    }
    if (email) {
        return email
    }
    return name || getAccessPersonLabel(entry)
}

/**
 * Tooltip pour le badge +N : tous les contacts non affichés (nom + email chacun).
 */
const getAccessOverflowTooltip = computed(() => {
    const entries = getVisibleToEntries.value
    if (entries.length <= VISIBLE_TO_OVERFLOW_THRESHOLD) {
        return ""
    }
    return entries
        .slice(VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW)
        .map((e) => getAccessPersonTooltipText(e))
        .join("\n\n")
})

/**
 * Options `v-tooltip` (contenu téléporté : classe globale pour retours à la ligne).
 */
function getAccessTooltipDirectiveOptions(value: string) {
    const v = value.trim()
    if (!v) {
        return { disabled: true as const }
    }
    return {
        value: v,
        class: "item-building-access-tooltip",
    }
}

/**
 * Libellé accessible (nom complet ou email).
 */
function getAccessPersonLabel(entry: BuildingVisibleToEntry): string {
    const name = getAccessPersonFullName(entry)
    if (name) return name
    if (entry.username) return entry.username
    return entry._id_user ?? entry._id ?? "Utilisateur"
}

/**
 * Couleur de fond des cercles sans photo (alternance comme BaseAccessIndicator legacy).
 * @param index - Position dans le groupe affiché (0-based)
 */
function getAccessAvatarCircleColor(index: number): string {
    const a = index === -1 ? 0 : index % 4
    switch (a) {
        case 1:
            /** Équivalent opaque de rgba(48, 66, 130, 0.18) sur fond blanc */
            return "#DADDE9"
        case 2:
            return "#FFD8BC"
        case 3:
            return "#FFEABC"
        default:
            /** Équivalent opaque de rgba(105, 212, 177, 0.62) sur fond blanc */
            return "#A2E4CF"
    }
}

/**
 * Passthrough PrimeVue pour appliquer la couleur au cercle (avatars initiales / +N).
 */
function getAccessAvatarLabelPt(index: number) {
    return {
        root: {
            style: {
                backgroundColor: getAccessAvatarCircleColor(index),
            },
        },
    }
}

/**
 * Initiales ou repli pour l’avatar sans image.
 */
function getAccessPersonInitials(entry: BuildingVisibleToEntry): string {
    const fn = (entry.firstname ?? "").trim()
    const ln = (entry.lastname ?? "").trim()
    if (fn && ln) {
        return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase()
    }
    if (fn.length >= 2) return fn.slice(0, 2).toUpperCase()
    if (fn.length === 1) return `${fn.charAt(0)}`.toUpperCase()
    const u = (entry.username ?? "").trim()
    if (u.includes("@")) {
        const local = u.split("@")[0] ?? ""
        if (local.length >= 2) return local.slice(0, 2).toUpperCase()
        if (local.length === 1) return local.toUpperCase()
    } else if (u.length >= 2) {
        return u.slice(0, 2).toUpperCase()
    }
    const id = entry._id_user ?? entry._id ?? ""
    const hex = id.replace(/[^a-fA-F0-9]/g, "")
    if (hex.length >= 2) return hex.slice(-2).toUpperCase()
    return "?"
}

/**
 * Construit les props d’un `Avatar` pour une entrée `visible_to`.
 */
function toAccessAvatarSlot(
    entry: BuildingVisibleToEntry,
    index: number
): AccessAvatarSlot {
    const id = entry._id_user ?? entry._id ?? ""
    const key = id || `visible-to-${index}`
    const img = entry.img?.trim()
    const image = img ? img : undefined
    const ariaLabel = getAccessPersonLabel(entry)
    return {
        key,
        image,
        label: image ? undefined : getAccessPersonInitials(entry),
        ariaLabel,
        entry,
    }
}

/**
 * Gère le clic sur le bâtiment
 */
function onClick(ev: MouseEvent): void {
    emit("click", props.building._id, ev)
}
</script>

<template>
    <component
        :is="props.nativeButton ? 'button' : 'div'"
        :type="props.nativeButton ? 'button' : undefined"
        class="item-building flex cursor-pointer items-start gap-2.5 py-0.5 hover:text-primary"
        :class="
            props.nativeButton
                ? 'm-0 w-full min-w-0 border-0 bg-transparent p-0 text-left font-inherit text-inherit'
                : ''
        "
        @click="onClick"
    >
        <BaseBuildingIcon
            class="shrink-0"
            :type="getBuildingIconType(props.building.niveau)"
            :size="28"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <span class="font-medium">{{ props.building.name || "-" }}</span>
            <div
                v-if="getHasOnlyViewsRights"
                class="flex items-center gap-1 min-w-0"
            >
                <span class="text-xs italic text-surface-600">
                    {{ t("header.views") }}
                </span>
                <span class="inline-flex shrink-0" @click.stop>
                    <BaseTooltipMessage
                        severity="secondary"
                        position="top"
                        size="extra-small"
                        :content="t('header.sharedBuildingHint')"
                    />
                </span>
            </div>
            <span class="text-sm text-surface-500">
                {{
                    props.building.address?.inline_name ??
                    (props.building.address
                        ? `${props.building.address.street || ""}, ${props.building.address.zipcode || ""} ${props.building.address.city || ""}`.trim()
                        : "")
                }}
            </span>
            <div
                v-if="showTags && getTagLabels.length"
                class="flex flex-wrap gap-1"
                @click.stop
            >
                <BaseTag
                    v-for="(label, idx) in getTagLabels"
                    :key="`${label}-${idx}`"
                    :value="label"
                    severity="secondary"
                    :pt="{ label: 'text-xs' }"
                />
            </div>
        </div>
        <BaseAvatarGroup
            v-if="getShowAccessAvatars"
            class="item-building__access-avatars shrink-0"
            @click.stop
        >
            <BaseAvatar
                v-for="(slot, slotIndex) in getAccessAvatarDisplay.slots"
                :key="slot.key"
                v-tooltip.top="
                    getAccessTooltipDirectiveOptions(
                        getAccessPersonTooltipText(slot.entry)
                    )
                "
                :image="slot.image"
                :label="slot.label"
                :aria-label="slot.ariaLabel"
                :pt="slot.image ? undefined : getAccessAvatarLabelPt(slotIndex)"
                shape="circle"
                size="normal"
            />
            <BaseAvatar
                v-if="getAccessAvatarDisplay.overflowExtra > 0"
                v-tooltip.top="
                    getAccessTooltipDirectiveOptions(getAccessOverflowTooltip)
                "
                :label="`+${getAccessAvatarDisplay.overflowExtra}`"
                :pt="
                    getAccessAvatarLabelPt(getAccessAvatarDisplay.slots.length)
                "
                shape="circle"
                size="normal"
                :aria-label="getAccessOverflowTooltip"
            />
        </BaseAvatarGroup>
    </component>
</template>

<style scoped>
/* Avatars d’accès : cercles compacts (plus petits que le défaut PrimeVue). */
.item-building__access-avatars :deep(.p-avatar) {
    --p-avatar-size: 2rem;
    --p-avatar-font-size: 0.8rem;
    width: var(--p-avatar-size);
    height: var(--p-avatar-size);
    min-width: var(--p-avatar-size);
    min-height: var(--p-avatar-size);
    font-size: var(--p-avatar-font-size);
}

.item-building__access-avatars :deep(.p-avatar-label) {
    line-height: 1;
    font-size: inherit;
}

.item-building__access-avatars :deep(.p-avatar-image) {
    object-fit: cover;
}
</style>

<!-- Tooltip téléporté (hors scope du composant) -->
<style>
.item-building-access-tooltip .p-tooltip-text {
    max-width: min(22rem, 85vw);
    text-align: left;
    white-space: pre-line;
}
</style>
